import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import { useCart } from '../contexts/CartContext';
import { Check, Minus, Plus } from 'lucide-react';
import { getModulesByCategory } from '../services/api';
import PriceSkeleton from './PriceSkeleton';
import {
  createFallbackModuleGroups,
  getCategoryContent,
  getModuleContent,
  getPricingCopy,
} from '../data/pricing';

const Modules = ({ headingLevel = 'h2' }) => {
  const { isRTL, currentLang } = useLanguage();
  const {
    pricingRegion,
    isPricingReady,
    formatPrice,
    localizeItemPrice,
    trackPricingEvent,
  } = usePricingRegion();
  const {
    addAddon,
    removeAddon,
    selectedAddons,
    updateAddonQuantity,
  } = useCart();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(0);
  const [loading, setLoading] = useState(true);
  const copy = getPricingCopy(currentLang);
  const HeadingTag = headingLevel;

  useEffect(() => {
    const loadModules = async () => {
      try {
        setCategories(await getModulesByCategory());
      } catch (error) {
        console.error('Error loading modules:', error);
        setCategories(createFallbackModuleGroups());
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, []);

  const getCategoryName = (group) => {
    const category = group.category || group;
    return (
      getCategoryContent(category.name, currentLang) ||
      category.name_translations?.[currentLang] ||
      category.name
    );
  };

  const getModuleDetails = (module) => {
    const centralContent = getModuleContent(module.name, currentLang);
    return {
      name:
        centralContent?.name ||
        module.name_translations?.[currentLang] ||
        module.name,
      impact:
        centralContent?.impact ||
        module.description_translations?.[currentLang] ||
        module.description ||
        '',
    };
  };

  const getSelectedAddon = (moduleId) =>
    selectedAddons.find((addon) => addon.id === moduleId);

  const handleModuleClick = (module) => {
    const selected = getSelectedAddon(module.id);
    const allowMultiple = module.name === 'Extra language';
    if (selected && !allowMultiple) {
      removeAddon(module.id);
      return;
    }

    const pricedModule = localizeItemPrice(module, 'MODULE');
    const details = getModuleDetails(module);
    addAddon({
      id: module.id,
      canonicalName: module.name,
      name: details.name,
      price: Number(pricedModule.price || 0),
      basePriceUsd: Number(module.price || 0),
      pricingRegion: pricingRegion.id,
      currencyCode: pricingRegion.currencyCode,
      allowMultiple,
      quantity: 1,
    });
    trackPricingEvent('module_selected', {
      itemKey: module.name,
      displayedAmount: Number(pricedModule.price || 0),
    });
  };

  return (
    <section
      id="modules"
      className="relative bg-black py-24 md:py-32"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className="text-center mb-12 md:mb-16">
          <HeadingTag className="text-4xl md:text-5xl lg:text-[48px] font-semibold text-white leading-[1.1] tracking-[-0.02em] mb-4">
            {copy.modulesTitle}
          </HeadingTag>
          <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto">
            {copy.modulesSubtitle}
          </p>
        </div>

        {loading || !isPricingReady ? (
          <PriceSkeleton cards={3} />
        ) : (
          <>
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
              {categories.map((category, index) => (
                <button
                  key={category.category?.id || index}
                  onClick={() => setActiveCategory(index)}
                  data-testid={`category-tab-${index}`}
                  className={`glass-tab ${
                    activeCategory === index ? 'active' : ''
                  }`}
                >
                  {getCategoryName(category)}
                </button>
              ))}
            </div>

            {categories[activeCategory] && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {(categories[activeCategory].modules || []).map(
                  (module, index) => {
                    const selectedAddon = getSelectedAddon(module.id);
                    const selected = Boolean(selectedAddon);
                    const quantity = selectedAddon?.quantity || 0;
                    const allowMultiple = module.name === 'Extra language';
                    const details = getModuleDetails(module);
                    const pricedModule = localizeItemPrice(module, 'MODULE');

                    return (
                      <div
                        key={module.id || index}
                        role="button"
                        tabIndex={0}
                        data-testid={`module-card-${module.id || index}`}
                        className={`group glass-card p-6 cursor-pointer min-w-0 ${
                          selected
                            ? 'ring-2 ring-[#00FFD1] bg-[#00FFD1]/5'
                            : ''
                        }`}
                        onClick={() => handleModuleClick(module)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleModuleClick(module);
                          }
                        }}
                        style={{ borderRadius: '12px' }}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <h4 className="text-white text-lg font-medium leading-tight min-w-0">
                            {details.name}
                          </h4>
                          <div
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-300 flex-shrink-0 ${
                              selected
                                ? 'bg-[#00FFD1] text-black'
                                : 'bg-white/10 text-white group-hover:bg-[#00FFD1]/20 group-hover:text-[#00FFD1]'
                            }`}
                          >
                            {selected ? <Check size={18} /> : <Plus size={18} />}
                          </div>
                        </div>
                        <p className="text-white/45 text-sm leading-relaxed min-h-[42px] mb-4">
                          {details.impact}
                        </p>
                        <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
                          <span className="price-value text-[#00FFD1] text-2xl font-bold">
                            {formatPrice(pricedModule.price)}
                          </span>
                          <span className="text-white/35 text-xs pb-1">
                            {copy.oneTime}
                          </span>
                        </div>

                        {selected && allowMultiple && (
                          <div
                            className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-3"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <span className="text-white/50 text-xs">
                              {copy.quantity}
                            </span>
                            <div className="quantity-control flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  updateAddonQuantity(module.id, quantity - 1)
                                }
                                className="w-8 h-8 rounded-lg bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
                                aria-label={copy.remove}
                              >
                                <Minus size={15} />
                              </button>
                              <span className="text-white font-bold min-w-6 text-center">
                                {quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  updateAddonQuantity(module.id, quantity + 1)
                                }
                                className="w-8 h-8 rounded-lg bg-[#00FFD1] text-black flex items-center justify-center hover:bg-[#00FFD1]/80"
                                aria-label={copy.quantity}
                              >
                                <Plus size={15} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Modules;
